import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar } from './snackbarSlice';
import {
  GetMessages,
  ReplyMessages,
  SendMessages,
  GetReplies,
  changeStatus,
  fetchNamesList,
  deleteMessageById,
  getNewMessageCount
} from '../../ApiServices/MessagesService';
import { handleResponse } from '../../ApiServices/ApiHandler';
import { t } from 'i18next';

export const initialState = {
  value: { totalCount: -1, data: [{}] },
  loading: false,
  error: null,
  rowsPerPage: 10,
  pageNo: 0,
  selectedValue: null,
  filter: {},
  name: [],
  newMessageCount: {}
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState: initialState,
  reducers: {
    clearMessagesState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.message = null;
      state.filter = initialState.filter;
      state.newMessageCount = initialState.newMessageCount;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchMessages.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchMessages.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(getReplies.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
    });
    builder.addCase(getReplies.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.error = action?.payload?.data;
      state.selectedValue = initialState.selectedValue;
    });
    builder.addCase(getNamesList.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.name = action?.payload?.data?.data;
    });
    builder.addCase(getNamesList.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.error = action?.payload?.data;
      state.name = initialState.name;
    });
    builder.addCase(fetchNewMessageCount.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.newMessageCount = action?.payload?.data;
    });
    builder.addCase(fetchNewMessageCount.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.error = action?.payload?.data;
      state.newMessageCount = initialState.newMessageCount;
    });
  }
});

export const messagesState = (state: RootState) => state?.messages;
export const { clearMessagesState } = messagesSlice.actions;
export default messagesSlice.reducer;

export const sendMessages = createAsyncThunk(
  'messages/sendMessages',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_MESSAGES') }));
      let response: any = await SendMessages(payload);
      return await handleResponse(response, fulfillWithValue, rejectWithValue, dispatch);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/getMessages',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_MESSAGES') }));
      return fulfillWithValue(await GetMessages(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const sendReply = createAsyncThunk(
  'messages/replyMessages',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('SENDING_REPLIES') }));
      let response: any = await ReplyMessages(payload);
      return await handleResponse(response, fulfillWithValue, rejectWithValue, dispatch);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const getReplies = createAsyncThunk(
  'messages/getReplies',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_REPLIES') }));
      return fulfillWithValue(await GetReplies(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const updateMessageStatus = createAsyncThunk(
  'messages/updateMessageStatus',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_MESSAGE_STATUS') }));
      return fulfillWithValue(await changeStatus(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const getNamesList = createAsyncThunk(
  'messages/getNamesList',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_NAMES') }));
      return fulfillWithValue(await fetchNamesList(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deleteMessageFromList = createAsyncThunk(
  'messages/deleteMessageFromList',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DELETING_MESSAGE') }));
      return fulfillWithValue(await deleteMessageById(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchNewMessageCount = createAsyncThunk(
  'messages/fetchNewMessageCount',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      //dispatch(showLoader({ message: t('DELETING_MESSAGE') }));
      return fulfillWithValue(await getNewMessageCount(payload));
    } catch (error) {
      //dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      //dispatch(hideLoader());
    }
  }
);
